import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AwayOverlay } from './AwayOverlay';
import { FocusGuardContext } from './FocusGuardContext';
import { FullscreenGate } from './FullscreenGate';
import { ScreenshotBlockedOverlay } from './ScreenshotBlockedOverlay';
import { useAwayDetection } from './useAwayDetection';
import { usePasteBlock } from './usePasteBlock';
import { useScreenshotGuard } from './useScreenshotGuard';

export const SIGNED_OUT_FLAG = 'focus_guard_signed_out';
export const SCREENSHOT_SIGN_OUT = 'screenshot';
const FORCE_END_TIMEOUT_MS = 8000;
const MIN_NOTICE_MS = 2000;

// App-wide anti-malpractice layer for learners (admins and Quality admins are exempt, and may take screenshots)
export const FocusGuardProvider = ({ children }) => {
    const { user, api, logout } = useAuth();
    const apiRef = useRef(api);
    apiRef.current = api;
    const logoutRef = useRef(logout);
    logoutRef.current = logout;
    const attemptRef = useRef(null);
    const [attempt, setAttempt] = useState(null);
    const [config, setConfig] = useState(null);
    const [endedLabel, setEndedLabel] = useState(null);

    useEffect(() => {
        if (!user?.id) { setConfig(null); return undefined; }
        let alive = true;
        apiRef.current.get('/focus-guard/config').then(({ data }) => alive && setConfig(data)).catch(() => alive && setConfig(null));
        return () => { alive = false; };
    }, [user?.id]);

    const settings = config?.settings;
    const guarded = Boolean(settings?.enabled && !config.exempt);

    const registerAttempt = useCallback((handle) => {
        attemptRef.current = handle;
        setAttempt({ label: handle.label });
        return () => {
            if (attemptRef.current !== handle) return;
            attemptRef.current = null;
            setAttempt(null);
        };
    }, []);

    // Ends any running attempt, then signs out; flag tells the login page why
    const endingRef = useRef(false);
    const signOutNow = useCallback(async (flag) => {
        if (endingRef.current) return;
        endingRef.current = true;
        const startedAt = Date.now();
        const end = attemptRef.current?.onForceEnd;
        setEndedLabel(attemptRef.current?.label || null);
        if (end) await Promise.race([Promise.resolve().then(end).catch(() => {}), new Promise((r) => setTimeout(r, FORCE_END_TIMEOUT_MS))]);
        await new Promise((r) => setTimeout(r, Math.max(0, MIN_NOTICE_MS - (Date.now() - startedAt))));
        sessionStorage.setItem(SIGNED_OUT_FLAG, flag);
        if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
        await logoutRef.current();
        window.location.assign('/login');
    }, []);
    const onLimit = useCallback((limit) => signOutNow(String(limit)), [signOutNow]);

    const exemptByRole = user?.role === 'admin' || user?.feature_access?.quality_admin === true;
    const shotGuarded = Boolean(user?.id) && (config ? !config.exempt : !exemptByRole);
    const resetShotRef = useRef(null);
    const onScreenshot = useCallback(async (kind, key) => {
        const attempt = attemptRef.current?.label || null;
        setEndedLabel(attempt);
        const res = await apiRef.current.post('/focus-guard/screenshot', { kind, key, page: window.location.pathname, attempt })
            .then((r) => r.data).catch(() => null);
        if (res?.ignored) { resetShotRef.current?.(); return; }
        await signOutNow(SCREENSHOT_SIGN_OUT);
    }, [signOutNow]);
    const { caught: shotCaught, reset: resetShot } = useScreenshotGuard({ enabled: shotGuarded, onCaught: onScreenshot });
    resetShotRef.current = resetShot;

    const { away, strikes, signingOut } = useAwayDetection({
        enabled: guarded, mouseLeave: Boolean(settings?.mouse_leave), limit: settings?.strike_limit || 3,
        initialStrikes: config?.strikes || 0, apiRef, attemptRef, onLimit,
    });
    usePasteBlock(guarded && Boolean(settings?.paste_block), apiRef);

    const value = useMemo(() => ({ registerAttempt }), [registerAttempt]);
    return (
        <FocusGuardContext.Provider value={value}>
            {children}
            {guarded && settings.fullscreen && attempt && <FullscreenGate attemptLabel={attempt.label} />}
            {guarded && !shotCaught && <AwayOverlay away={away} signingOut={signingOut} strikes={strikes} limit={settings.strike_limit} attemptLabel={signingOut ? endedLabel : attempt?.label} />}
            {shotGuarded && shotCaught && <ScreenshotBlockedOverlay attemptLabel={endedLabel} />}
        </FocusGuardContext.Provider>
    );
};
